import { useState } from "react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { z } from "zod";
import {
  AdminOrganizationControllerCreateRequestBody,
  AdminOrganizationControllerCreateResponse,
  useAdminOrganizationControllerCreate,
} from "service";
import { queryKeyFn } from "service/api/estabrookContext";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { queryClient, skipGlobalErrorToast } from "@/query-client";
import useAddressValidation from "@/hooks/use-address-validation";
import { optionalEmailSchema } from "@/shared/utils/email-validation";
import { AddOrganizationFormValues } from "../types/organization-form";
import {
  buildCreateOrganizationFormData,
  getErrorDetail,
  getHomeImageErrorMessage,
  getPromotionalBannerInitialValues,
  promotionalBannerValidationSchema,
} from "../utils/promotional-banner";

const initialValues: AddOrganizationFormValues = {
  name: "",
  state: "",
  address: "",
  city: "",
  zipCode: "",
  replyToEmail: "",
  ...getPromotionalBannerInitialValues(),
};

const useAddOrganizationForm = (
  onFormSuccess: (data: AdminOrganizationControllerCreateResponse) => void,
) => {
  const {
    validateAddress,
    getPlacePredictions,
    placePredictions,
    getAddressComponents,
  } = useAddressValidation({ bounds: null });

  // Name/state are required by the backend; address is
  // optional on the backend but required per the Figma form and ticket
  // ("Required fields are marked with *": Name, Address, State). Address
  // must also match a live Places prediction (ESR-1632) - city/state/zip are
  // derived from that same selection and aren't independently editable.
  const validationSchema = z.intersection(
    z.object({
      name: z.string().min(1, "Organization name is required"),
      address: z
        .string()
        .min(1, "Address is required")
        .refine(validateAddress, { message: "Invalid address." }),
      state: z.string().min(1, "State is required"),
      city: z.string().optional().nullable(),
      zipCode: z.string().optional().nullable(),
      replyToEmail: optionalEmailSchema.optional(),
    }),
    promotionalBannerValidationSchema,
  );

  const [homeImageError, setHomeImageError] = useState<string | null>(null);

  const { mutate, isLoading } = useAdminOrganizationControllerCreate({
    onSuccess: (data) => {
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/organizations",
          operationId: "adminOrganizationControllerFindMany",
          variables: {},
        }),
      );
      onFormSuccess(data);
    },
    meta: skipGlobalErrorToast,
    onError: (error) => {
      const imageError = getHomeImageErrorMessage(error);
      if (imageError) {
        // Show it in the drop zone and drop the rejected file. Saving stays
        // blocked until another file is picked or the image is dropped.
        setHomeImageError(imageError);
        organizationForm.setFieldValue("homeImageFile", null);
      }
      // One toast with the backend's reason (the global handler is skipped).
      toast.error(
        getErrorDetail(error) ??
          "Couldn't create the organization. Please try again.",
      );
    },
  });

  const organizationForm = useFormik<AddOrganizationFormValues>({
    initialValues,
    validationSchema: toFormikValidationSchema(validationSchema),
    onSubmit: (values) => {
      setHomeImageError(null);
      mutate({
        // The generated body type can't express FormData.
        body: buildCreateOrganizationFormData(
          values,
        ) as unknown as AdminOrganizationControllerCreateRequestBody,
        headers: { "Content-Type": "multipart/form-data" },
      });
    },
  });

  return {
    organizationForm,
    homeImageError,
    clearHomeImageError: () => setHomeImageError(null),
    isLoading,
    getPlacePredictions,
    placePredictions,
    getAddressComponents,
  };
};

export default useAddOrganizationForm;
